import { Select, Form, Row, Col, Input, Button, Switch, Table, Upload, Tag, Alert } from 'antd'
import { useQuery } from '@apollo/react-hooks'
import React, { useContext, useEffect, useState } from 'react'
import map from 'lodash/map'
import get from 'lodash/get'
import { UploadOutlined, SendOutlined } from '@ant-design/icons'

import client from 'apollo'
import Page404 from 'components/Page404'
import { GET_MESSAGES, GET_BIRTHDAY_LOGS } from './graphql/Queries'
import openNotification from 'utils/Notification'
import { UPDATE_MESSAGE, SEND_MANUAL_BIRTHDAY_NOTIFICATION, UPLOAD_BIRTHDAY_IMAGE } from './graphql/Mutations'
import './index.css'
import CustomMeta from 'components/CustomMeta'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserSave } from 'utils/User'

const { Option } = Select

const formItemLayout = {
  labelCol: { xs: { span: 24 }, sm: { span: 24 } },
  wrapperCol: { xs: { span: 24 }, sm: { span: 24 } },
}

const tailFormItemLayout = {
  wrapperCol: { xs: { span: 24, offset: 0 }, sm: { span: 16, offset: 8 } },
}

export default function () {
  const { state: { currentUser, userPermissions, isClient } = {} } =
    useContext(AppContext) || {}
  const canEdit = canUserEdit(userPermissions || currentUser, isClient)
  const canSave = canUserSave(userPermissions || currentUser, isClient)
  const canModify = canEdit && canSave

  const [form] = Form.useForm()
  const [selected, setSelected] = useState(undefined)
  const [formLoading, setFormLoading] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [sendingUserId, setSendingUserId] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)

  const { data, loading, error } = useQuery(GET_MESSAGES, {
    fetchPolicy: 'network-only',
  })

  const {
    data: birthdayLogsData,
    loading: birthdayLogsLoading,
    refetch: refetchBirthdayLogs,
  } = useQuery(GET_BIRTHDAY_LOGS, {
    fetchPolicy: 'network-only',
    skip: selected?.type !== 'BIRTHDAY',
  })

  let messages
  if (error) return <Page404 error={error} />
  if (!loading && get(data, 'getAllMessages')) {
    messages = data.getAllMessages.map((msg, key) => ({
      key: key.toString(),
      ...msg,
    }))
  }

  const excludeMessages = ['TICKET', 'MONTHLY_REPORT']

  useEffect(() => {
    if (messages && !selected) {
      const newValues = messages[0]
      setSelected(newValues)
      setImageUrl(newValues?.imageUrl || null)
      form.setFieldsValue(newValues)
    }
  }, [form, messages, selected])

  function handleChange(value) {
    const newValues = messages.filter((message) => message.type === value)[0]
    setSelected(newValues)
    setImageUrl(newValues?.imageUrl || null)
    form.setFieldsValue(newValues)
  }

  const onFinish = (values) => {
    if (!canModify) {
      openNotification('error', 'You do not have permission to modify messages.')
      return
    }
    const where = { id: parseInt(selected.id), type: selected.type }
    setFormLoading(true)
    const payload = {
      ...values,
      imageUrl,
    }
    client
      .mutate({
        mutation: UPDATE_MESSAGE,
        variables: { data: payload, where },
        refetchQueries: [{ query: GET_MESSAGES, fetchPolicy: 'network-only' }],
      })
      .then((res) => {
        openNotification('success', 'Message Updated Successfully')
      })
      .catch((err) => {
        console.log(err)
        openNotification('error', 'Failed to update message')
      })
      .finally(() => setFormLoading(false))
  }

  const handleImageUpload = (file) => {
    const rawFile = file?.originFileObj || file
    const isLt3M = rawFile && rawFile.size / 1024 / 1024 <= 3

    if (!isLt3M) {
      openNotification('error', 'Image file size must be 3MB or smaller!')
      return false
    }

    setUploadingImage(true)
    client
      .mutate({
        mutation: UPLOAD_BIRTHDAY_IMAGE,
        variables: { file: rawFile },
        refetchQueries: [{ query: GET_MESSAGES, fetchPolicy: 'network-only' }],
      })
      .then((res) => {
        const uploadedUrl = res?.data?.uploadBirthdayImage
        if (uploadedUrl) {
          setImageUrl(uploadedUrl)
          openNotification('success', 'Birthday Image Uploaded Successfully')
        }
      })
      .catch((err) => {
        console.log(err)
        openNotification('error', 'Failed to upload image')
      })
      .finally(() => setUploadingImage(false))
    return false
  }

  const handleSendManualNotification = (userId) => {
    if (!canModify) {
      openNotification('error', 'You do not have permission to send notifications.')
      return
    }
    setSendingUserId(userId)
    client
      .mutate({
        mutation: SEND_MANUAL_BIRTHDAY_NOTIFICATION,
        variables: { userId },
      })
      .then(() => {
        openNotification('success', 'Birthday Notification Sent Successfully')
        refetchBirthdayLogs()
      })
      .catch((err) => {
        console.log(err)
        openNotification('error', 'Failed to send birthday notification')
      })
      .finally(() => setSendingUserId(null))
  }

  const logColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'User Name',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Birth Date',
      dataIndex: 'birthDate',
      key: 'birthDate',
    },
    {
      title: 'Email Notification',
      dataIndex: 'sentTimestamp',
      key: 'sentTimestamp',
      render: (text) =>
        text === 'no' ? (
          <Tag color='orange'>Not Sent</Tag>
        ) : (
          <Tag color='green'>{text}</Tag>
        ),
    },
    {
      title: 'Popup Status',
      dataIndex: 'visitedTimestamp',
      key: 'visitedTimestamp',
      render: (text, record) =>
        record.popupVisited ? (
          <Tag color='blue'>{text}</Tag>
        ) : (
          <Tag color='orange'>Not Visited</Tag>
        ),
    },
    {
      title: 'Send Notification',
      key: 'action',
      render: (_, record) => (
        <Button
          type='primary'
          size='small'
          icon={<SendOutlined />}
          loading={sendingUserId === record.userId}
          disabled={!canModify}
          onClick={() => handleSendManualNotification(record.userId)}
          style={{
            backgroundColor: '#bd081c',
            borderColor: '#bd081c',
            borderRadius: '4px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            padding: '4px 12px',
            height: '28px',
            fontSize: '12px',
          }}
        >
          Send
        </Button>
      ),
    },
  ]

  const birthdayLogs = get(birthdayLogsData, 'getBirthdayLogs', [])

  return (
    <div className='main_dashboard_div'>
      <CustomMeta title='Messages' />
      <div className='agreement_main_div'>
        <div className='agreement_text'>Messages</div>
      </div>

      <Select
        getPopupContainer={(trigger) => trigger.parentElement}
        value={selected?.type}
        onChange={handleChange}
        style={{ minWidth: 250, marginBottom: 20 }}
        placeholder='Select Message'
      >
        {map(messages, (message, key) => {
          const { title, type } = message
          return (
            <Option value={type} key={key}>
              {title}
            </Option>
          )
        })}
      </Select>
      {selected && (
        <Form
          {...formItemLayout}
          form={form}
          name='message'
          onFinish={onFinish}
          className='user-form'
          scrollToFirstError
        >
          {!canModify && (
            <div style={{ marginBottom: 16 }}>
              <Alert
                message='Read-Only Access: You do not have permission to edit or save messages.'
                type='warning'
                showIcon
              />
            </div>
          )}
          <Row gutter={16}>
            {!excludeMessages.includes(selected?.type) && (
              <Col className='gutter-row message-format' span={24}>
                <Form.Item
                  name='title'
                  label='Title'
                  rules={[
                    { required: true, message: 'Please input your Title!' },
                  ]}
                >
                  <Input placeholder='Enter Message Title' disabled={!canModify} />
                </Form.Item>
              </Col>
            )}

            <Col className='gutter-row message-format' span={24}>
              <Form.Item label='Active' name='isActive' valuePropName='checked'>
                <Switch disabled={!canModify} />
              </Form.Item>
            </Col>

            {selected?.type === 'BIRTHDAY' && (
              <Col className='gutter-row message-format' span={24}>
                <Form.Item label='Birthday Image Banner'>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Upload
                        beforeUpload={handleImageUpload}
                        showUploadList={false}
                        accept='image/*'
                        disabled={!canModify}
                      >
                        <Button icon={<UploadOutlined />} loading={uploadingImage} disabled={!canModify}>
                          Upload Birthday Image
                        </Button>
                      </Upload>
                      <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                        (Max file size: 3MB)
                      </span>
                    </div>
                    {imageUrl && (
                      <div style={{ marginTop: 10 }}>
                        <img
                          src={imageUrl}
                          alt='Birthday Banner'
                          style={{
                            maxWidth: 300,
                            maxHeight: 200,
                            borderRadius: 8,
                            border: '1px solid #d9d9d9',
                            objectFit: 'cover',
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Form.Item>
              </Col>
            )}

            {!excludeMessages.includes(selected?.type) && (
              <Col className='gutter-row message-format' span={24}>
                <Form.Item
                  name='description'
                  label='description'
                  rules={[
                    {
                      required: true,
                      message: 'Please input your Description!',
                    },
                  ]}
                >
                  <Input.TextArea rows={5} placeholder='Enter Description' disabled={!canModify} />
                </Form.Item>
              </Col>
            )}

            <Col className='gutter-row message-format' span={24}>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={formLoading}
                  className='agreement_submit_btn'
                  disabled={!canModify}
                >
                  Save
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}

      {selected?.type === 'BIRTHDAY' && (
        <div style={{ marginTop: 30 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 15,
              color: '#1f1f1f',
            }}
          >
            Today's Birthday Log
          </div>
          <Table
            columns={logColumns}
            dataSource={birthdayLogs}
            rowKey='userId'
            loading={birthdayLogsLoading}
            pagination={{ pageSize: 10 }}
            style={{ overflowX: 'auto' }}
          />
        </div>
      )}
    </div>
  )
}
